import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Chip, IconButton, Tooltip, Checkbox,
  CircularProgress, Alert, Snackbar
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import { getPlans, createPlan, updatePlan, deletePlan } from "../api/adminApi";

const FEATURE_LABELS = {
  lead_generation: "Lead Generation Bot", customer_support: "Customer Support Bot",
  live_chat: "Live Chat", canned_responses: "Canned Responses",
  analytics: "Chatbot Analytics", template_message_analytics: "Template Message Analytics",
  webhook_integrations: "Webhook Integrations", google_sheets: "Google Sheets Integration",
  crm_standard: "CRM (Standard)", email_otp: "Email OTP Validations",
  lead_notifications: "Lead Email Notifications", hot_lead_notifications: "Hot Lead Notifications",
  auto_email_followup: "Auto Email Followup", multilingual: "Multi-Lingual (2 Languages)",
  ai_bot: "AI Bot (Self Setup)", ai_speech: "AI Speech Answering",
  voice_intent_mapping: "Voice Enabled Intent Mapping", csv_export: "CSV Chats Export",
  contacts_dashboard: "Contacts Dashboard", whatsapp_catalog: "WhatsApp Catalog",
  whatsapp_ecommerce: "WhatsApp E-Commerce", "2fa": "2 Factor Authentication",
  lead_data_masking: "Lead Data Masking",
};

const ALL_FEATURES = Object.keys(FEATURE_LABELS);

const EMPTY_PLAN = {
  name: "", slug: "", price: 0, sortOrder: 0, isActive: true,
  limits: { chatbots: 1, chatsPerMonth: 100, waUserChatsPerMonth: 0, liveAgents: 0, languages: 1 },
  features: [],
};

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PLAN });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getPlans();
      setPlans(Array.isArray(data) ? data : []);
    } catch { setSnackbar({ open: true, message: "Failed to load plans", severity: "error" });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPlans(); }, []);

  const openCreate = () => { setForm({ ...EMPTY_PLAN }); setEditingId(null); setDialog(true); };

  const openEdit = (plan) => {
    setForm({
      name: plan.name, slug: plan.slug, price: plan.price, sortOrder: plan.sortOrder,
      isActive: plan.isActive,
      limits: { ...plan.limits },
      features: [...(plan.features || [])],
    });
    setEditingId(plan._id);
    setDialog(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("limits.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({ ...prev, limits: { ...prev.limits, [key]: type === "number" ? Number(value) : value } }));
    } else {
      setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value }));
    }
  };

  const toggleFeature = (feature) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        const data = await updatePlan(editingId, form);
        setPlans((prev) => prev.map((p) => (p._id === editingId ? { ...p, ...data } : p)));
        setSnackbar({ open: true, message: "Plan updated", severity: "success" });
      } else {
        const created = await createPlan(form);
        setPlans((prev) => [...prev, created]);
        setSnackbar({ open: true, message: "Plan created", severity: "success" });
      }
      setDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Failed to save plan", severity: "error" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await deletePlan(deleteId);
      setPlans((prev) => prev.filter((p) => p._id !== deleteId));
      setSnackbar({ open: true, message: "Plan deleted", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to delete plan", severity: "error" });
    } finally { setDeleteId(null); }
  };

  const limitVal = (plan, key) => {
    if (!plan.limits) return "0";
    const v = plan.limits[key];
    if (v === -1) return "∞";
    if (key === "chatsPerMonth" || key === "waUserChatsPerMonth") return v.toLocaleString();
    return v;
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Plans</Typography>
          <Typography variant="body2" color="#64748b">{plans.length} active plans</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
          Create Plan
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {plans.map((plan) => (
          <Paper key={plan._id} className="glass-card" sx={{ width: 300, position: "relative" }}>
            <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(plan)} sx={{ color: "#6366f1", bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#6366f112" } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(plan._id)} sx={{ color: "#ef4444", bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#fef2f2" } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Box sx={{ p: 2.5, textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>
              <Typography fontWeight={700} fontSize={18} color="#1e293b">{plan.name}</Typography>
              <Typography fontWeight={800} fontSize={28} color="#1e293b">${plan.price}<Typography component="span" fontSize={13} color="#64748b">/mo</Typography></Typography>
              {!plan.isActive && <Chip label="Inactive" size="small" color="error" sx={{ mt: 0.5 }} />}
            </Box>
            <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
              {["chatbots", "chatsPerMonth", "waUserChatsPerMonth", "liveAgents", "languages"].map((key) => (
                <Box key={key} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography variant="body2" color="#475569" sx={{ textTransform: "capitalize" }}>{key.replace(/([A-Z])/g, " $1")}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#1e293b">{limitVal(plan, key)}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ p: 2, maxHeight: 220, overflow: "auto" }}>
              {(plan.features || []).map((f) => (
                <Box key={f} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.3 }}>
                  <CheckIcon sx={{ fontSize: 14, color: "#10b981" }} />
                  <Typography variant="body2" color="#475569">{FEATURE_LABELS[f] || f}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>{editingId ? "Edit Plan" : "Create Plan"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required size="small"
              sx={{ flex: "1 1 200px" }} InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
              InputLabelProps={{ sx: { color: "#64748b" } }} />
            <TextField label="Slug" name="slug" value={form.slug} onChange={handleChange} required size="small"
              sx={{ flex: "1 1 200px" }} InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
              InputLabelProps={{ sx: { color: "#64748b" } }} />
            <TextField label="Price ($)" name="price" type="number" value={form.price} onChange={handleChange} size="small"
              sx={{ flex: "1 1 120px" }} InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
              InputLabelProps={{ sx: { color: "#64748b" } }} />
            <TextField label="Sort Order" name="sortOrder" type="number" value={form.sortOrder} onChange={handleChange} size="small"
              sx={{ flex: "1 1 120px" }} InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
              InputLabelProps={{ sx: { color: "#64748b" } }} />
          </Box>

          <Typography fontWeight={600} color="#475569" fontSize={13} sx={{ mt: 2, mb: 1 }}>Limits</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {["chatbots", "chatsPerMonth", "waUserChatsPerMonth", "liveAgents", "languages"].map((key) => (
              <TextField key={key} label={key.replace(/([A-Z])/g, " $1")} name={`limits.${key}`} type="number"
                value={form.limits?.[key] ?? 0} onChange={handleChange} size="small"
                sx={{ flex: "1 1 140px" }} InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
                InputLabelProps={{ sx: { color: "#64748b" } }} />
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <FormControlLabel control={<Switch name="isActive" checked={form.isActive} onChange={handleChange} sx={{ "& .MuiSwitch-thumb": { bgcolor: form.isActive ? "#6366f1" : "#cbd5e1" } }} />}
              label={<Typography variant="body2" color="#475569">Active</Typography>} />
          </Box>

          <Typography fontWeight={600} color="#475569" fontSize={13} sx={{ mt: 2, mb: 1 }}>Features</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {ALL_FEATURES.map((f) => (
              <Chip key={f} label={FEATURE_LABELS[f]} clickable variant={form.features.includes(f) ? "filled" : "outlined"}
                icon={form.features.includes(f) ? <CheckIcon /> : undefined}
                onClick={() => toggleFeature(f)}
                sx={{ color: form.features.includes(f) ? "#fff" : "#475569", borderColor: "#e2e8f0", bgcolor: form.features.includes(f) ? "#6366f1" : "transparent", "&:hover": { bgcolor: form.features.includes(f) ? "#4f46e5" : "#f1f5f9" } }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(false)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.name || !form.slug}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}
        PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important", maxWidth: 400 } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Delete Plan</DialogTitle>
        <DialogContent><Typography color="#475569">Are you sure? This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;
